import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Avatar,
  IconButton,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { authAPI } from '../../services/api';
import { formatFullName, getInitials } from '../../utils/nameUtils';

const ClientSettings = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [profileData, setProfileData] = useState({
    prefix: '',
    firstName: '',
    lastName: '',
    suffix: '',
    name: currentUser?.name || 'Healthcare Provider',
    email: currentUser?.email || '',
    phone: '',
    npi: '',
    clinicName: '',
    dba: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    status: 'ACTIVE',
    registrationDate: new Date().toISOString(),
    id: currentUser?.id || ''
  });
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [saveAlert, setSaveAlert] = useState(false);
  const [error, setError] = useState('');

  // Fetch full user profile on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await authAPI.getCurrentUser();
        
        if (response.success && response.data) {
          const user = response.data;
          const profile = user.profile || {};
          
          // Map user data to profileData format
          setProfileData({
            prefix: user.prefix || '',
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            suffix: user.suffix || '',
            name: formatFullName(user) || 'Healthcare Provider',
            email: user.email || '',
            phone: user.phone || '',
            npi: profile.licenseNumber || '', // Using licenseNumber as Medical License Number
            clinicName: profile.clinicName || '',
            dba: '', // DBA not in schema yet
            address: {
              street: profile.address || '',
              city: profile.city || '',
              state: profile.state || '',
              zipCode: profile.zipCode || ''
            },
            status: user.status || 'ACTIVE',
            registrationDate: user.createdAt || new Date().toISOString(),
            id: user.id || ''
          });
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('Failed to load profile data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleEdit = (section) => {
    setEditingSection(section);
  };

  const handleSave = (section) => {
    // Simulate saving data
    setEditingSection(null);
    setSaveAlert(true);
    setTimeout(() => setSaveAlert(false), 3000);
  };

  const handleCancel = (section) => {
    // Reload original data from API
    const fetchUserProfile = async () => {
      try {
        const response = await authAPI.getCurrentUser();
        if (response.success && response.data) {
          const user = response.data;
          const profile = user.profile || {};
          setProfileData({
            prefix: user.prefix || '',
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            suffix: user.suffix || '',
            name: formatFullName(user) || 'Healthcare Provider',
            email: user.email || '',
            phone: user.phone || '',
            npi: profile.licenseNumber || '',
            clinicName: profile.clinicName || '',
            dba: '',
            address: {
              street: profile.address || '',
              city: profile.city || '',
              state: profile.state || '',
              zipCode: profile.zipCode || ''
            },
            status: user.status || 'ACTIVE',
            registrationDate: user.createdAt || new Date().toISOString(),
            id: user.id || ''
          });
        }
      } catch (err) {
        console.error('Error reloading profile:', err);
      }
    };
    fetchUserProfile();
    setEditingSection(null);
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      // Handle nested fields like address.street
      const [parent, child] = field.split('.');
      setProfileData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handlePasswordChange = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return;
    }
    // Simulate password change
    setPasswordModalOpen(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setSaveAlert(true);
    setTimeout(() => setSaveAlert(false), 3000);
  };

  const SettingsSection = ({ title, icon, children, editable = true, sectionKey }) => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {React.cloneElement(icon, { sx: { mr: 2, color: '#0d9488' } })}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          {editable && (
            <Box>
              {editingSection === sectionKey ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton size="small" onClick={() => handleSave(sectionKey)} color="primary">
                    <SaveIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleCancel(sectionKey)}>
                    <CancelIcon />
                  </IconButton>
                </Box>
              ) : (
                <IconButton size="small" onClick={() => handleEdit(sectionKey)}>
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
          Account Settings
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Manage your profile information and account preferences
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Save Alert */}
      {saveAlert && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          {/* Personal Information */}
          <SettingsSection title="Personal Information" icon={<PersonIcon />} sectionKey="personal">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Prefix"
                  value={profileData.prefix}
                  onChange={(e) => {
                    handleInputChange('prefix', e.target.value);
                    // Update name when prefix changes
                    const newName = formatFullName({
                      prefix: e.target.value,
                      firstName: profileData.firstName,
                      lastName: profileData.lastName,
                      suffix: profileData.suffix
                    });
                    handleInputChange('name', newName);
                  }}
                  disabled={editingSection !== 'personal'}
                  placeholder="Dr., Mr., Ms., etc."
                  helperText="e.g., Dr."
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={profileData.firstName}
                  onChange={(e) => {
                    handleInputChange('firstName', e.target.value);
                    // Update name when first name changes
                    const newName = formatFullName({
                      prefix: profileData.prefix,
                      firstName: e.target.value,
                      lastName: profileData.lastName,
                      suffix: profileData.suffix
                    });
                    handleInputChange('name', newName);
                  }}
                  disabled={editingSection !== 'personal'}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={profileData.lastName}
                  onChange={(e) => {
                    handleInputChange('lastName', e.target.value);
                    // Update name when last name changes
                    const newName = formatFullName({
                      prefix: profileData.prefix,
                      firstName: profileData.firstName,
                      lastName: e.target.value,
                      suffix: profileData.suffix
                    });
                    handleInputChange('name', newName);
                  }}
                  disabled={editingSection !== 'personal'}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Suffix"
                  value={profileData.suffix}
                  onChange={(e) => {
                    handleInputChange('suffix', e.target.value);
                    // Update name when suffix changes
                    const newName = formatFullName({
                      prefix: profileData.prefix,
                      firstName: profileData.firstName,
                      lastName: profileData.lastName,
                      suffix: e.target.value
                    });
                    handleInputChange('name', newName);
                  }}
                  disabled={editingSection !== 'personal'}
                  placeholder="DVM, PhD, etc."
                  helperText="e.g., DVM, PhD"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={editingSection !== 'personal'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={profileData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={editingSection !== 'personal'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Medical License Number"
                  value={profileData.npi}
                  onChange={(e) => handleInputChange('npi', e.target.value)}
                  disabled={editingSection !== 'personal'}
                  helperText="Enter your state medical license number"
                />
              </Grid>
            </Grid>
          </SettingsSection>

          {/* Clinic Information */}
          <SettingsSection title="Clinic Information" icon={<BusinessIcon />} sectionKey="clinic">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Clinic Name"
                  value={profileData.clinicName}
                  onChange={(e) => handleInputChange('clinicName', e.target.value)}
                  disabled={editingSection !== 'clinic'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="DBA (Doing Business As)"
                  value={profileData.dba}
                  onChange={(e) => handleInputChange('dba', e.target.value)}
                  disabled={editingSection !== 'clinic'}
                  helperText="If your clinic operates under a different business name"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={profileData.address.street}
                  onChange={(e) => handleInputChange('address.street', e.target.value)}
                  disabled={editingSection !== 'clinic'}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={profileData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  disabled={editingSection !== 'clinic'}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={profileData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  disabled={editingSection !== 'clinic'}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={profileData.address.zipCode}
                  onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                  disabled={editingSection !== 'clinic'}
                />
              </Grid>
            </Grid>
          </SettingsSection>

          {/* Security Settings */}
          <SettingsSection title="Security" icon={<SecurityIcon />} editable={false}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Password
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last changed: 30 days ago
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => setPasswordModalOpen(true)}
                  >
                    Change Password
                  </Button>
                </Box>
                <Divider />
              </Grid>
              
              <Grid item xs={12}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                    Account Status
                  </Typography>
                  <Chip label={profileData.status} color="success" />
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                    Registration Date
                  </Typography>
                  <Typography variant="body2">
                    {new Date(profileData.registrationDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </SettingsSection>

          {/* Notification Preferences */}
          <SettingsSection title="Notifications" icon={<NotificationsIcon />} editable={false}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Email notifications are automatically enabled</strong> for order confirmations, 
                shipping updates, and important account information. You cannot disable these notifications 
                as they are required for compliance and order tracking.
              </Typography>
            </Alert>
          </SettingsSection>
        </Grid>

        {/* Profile Summary Sidebar */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ position: 'sticky', top: 24 }}>
            <CardContent>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: '#0d9488',
                    fontSize: '1.5rem',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  {getInitials(profileData)}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {profileData.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profileData.clinicName}
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ mr: 2, color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">{profileData.email}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ mr: 2, color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">{profileData.phone}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationIcon sx={{ mr: 2, color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">
                    {profileData.address.city}, {profileData.address.state}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Account Information
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Medical License:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {profileData.npi}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Status:</Typography>
                  <Chip label={profileData.status} color="success" size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Member Since:</Typography>
                  <Typography variant="body2">
                    {new Date(profileData.registrationDate).getFullYear()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Change Password Modal */}
      <Dialog open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Current Password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="New Password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                helperText="Password must be at least 8 characters long"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Confirm New Password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                error={passwordData.newPassword !== passwordData.confirmPassword && passwordData.confirmPassword !== ''}
                helperText={
                  passwordData.newPassword !== passwordData.confirmPassword && passwordData.confirmPassword !== ''
                    ? 'Passwords do not match'
                    : ''
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={
              !passwordData.currentPassword ||
              !passwordData.newPassword ||
              passwordData.newPassword !== passwordData.confirmPassword ||
              passwordData.newPassword.length < 8
            }
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientSettings; 